import React from 'react';
import { Settings, Zap, Activity, Snowflake, Building, X, Users, Network, Globe, BarChart2, Layers, Cable, FileText, Leaf, CreditCard, Server, Lock } from 'lucide-react';

interface ManagementPanelProps {
  isOpen: boolean;
  onClose: () => void;
  setShowUpgrades: (show: boolean) => void;
  setShowPowerManagementModal: (show: boolean) => void;
  setShowGoals: (show: boolean) => void;
  setShowChillerManagementModal: (show: boolean) => void;
  setShowSiteLayoutEditor: (show: boolean) => void;
  setShowStaffModal: (show: boolean) => void;
  setShowNetworkModal: (show: boolean) => void;
  setShowMmrModal: (show: boolean) => void;
  setShowFinancials: (show: boolean) => void;
  setShowFloorModal: (show: boolean) => void;
  setShowBackboneModal: (show: boolean) => void;
  setShowContractsModal: (show: boolean) => void;
  setShowGreenEnergyModal: (show: boolean) => void;
  setShowLoansModal: (show: boolean) => void;
  setShowFleetOverview?: (show: boolean) => void;
  staffCount?: number;
  activeContractCount?: number;
  activeLoanCount?: number;
  // Progressive-disclosure signals — advanced tools stay dimmed+locked until relevant.
  unlockHasServer?: boolean;      // first server installed
  unlockEarning?: boolean;        // datacenter is connected / earning
  unlockSecondLocation?: boolean; // owns a 2nd datacenter
}

interface Tool {
  key: string;
  label: string;
  icon: React.ReactNode;
  onOpen: (v: boolean) => void;
  locked?: boolean;
  lockHint?: string;
  badge?: React.ReactNode;
}

const HINT_SERVER = 'Install a server to unlock';
const HINT_EARNING = "Unlocks once you're earning";
const HINT_SECOND = 'Unlocks with a 2nd datacenter';

const ManagementPanel: React.FC<ManagementPanelProps> = ({
  isOpen,
  onClose,
  setShowUpgrades,
  setShowPowerManagementModal,
  setShowGoals,
  setShowChillerManagementModal,
  setShowSiteLayoutEditor,
  setShowStaffModal,
  setShowNetworkModal,
  setShowMmrModal,
  setShowFinancials,
  setShowFloorModal,
  setShowBackboneModal,
  setShowContractsModal,
  setShowGreenEnergyModal,
  setShowLoansModal,
  setShowFleetOverview,
  staffCount = 0,
  activeContractCount = 0,
  activeLoanCount = 0,
  unlockHasServer = true,
  unlockEarning = true,
  unlockSecondLocation = true,
}) => {
  const open = (fn: (v: boolean) => void) => {
    fn(true);
    onClose();
  };

  const badge = (count: number, cls: string) =>
    count > 0 ? <span className={`ml-auto text-xs ${cls}`}>{count}</span> : undefined;

  // Build tier (Power/Cooling/Network/Connectivity) is always available — it's the
  // mandatory chain the Setup Guide walks players through.
  const infrastructure: Tool[] = [
    { key: 'power', label: 'Power', icon: <Zap size={16} className="text-yellow-400 shrink-0" />, onOpen: setShowPowerManagementModal },
    { key: 'network', label: 'Data Network', icon: <Network size={16} className="text-indigo-400 shrink-0" />, onOpen: setShowNetworkModal },
    { key: 'connectivity', label: 'Connectivity', icon: <Globe size={16} className="text-cyan-400 shrink-0" />, onOpen: setShowMmrModal },
    { key: 'cooling', label: 'Cooling', icon: <Snowflake size={16} className="text-cyan-400 shrink-0" />, onOpen: setShowChillerManagementModal },
    { key: 'floor', label: 'Floor Quality', icon: <Layers size={16} className="text-stone-400 shrink-0" />, onOpen: setShowFloorModal, locked: !unlockHasServer, lockHint: HINT_SERVER },
    { key: 'green', label: 'Green Energy', icon: <Leaf size={16} className="text-green-400 shrink-0" />, onOpen: setShowGreenEnergyModal, locked: !unlockEarning, lockHint: HINT_EARNING },
    { key: 'backbone', label: 'Backbone', icon: <Cable size={16} className="text-violet-400 shrink-0" />, onOpen: setShowBackboneModal, locked: !unlockSecondLocation, lockHint: HINT_SECOND },
  ];

  const operations: Tool[] = [
    { key: 'staff', label: 'Staff', icon: <Users size={16} className="text-rose-400 shrink-0" />, onOpen: setShowStaffModal, locked: !unlockHasServer, lockHint: HINT_SERVER, badge: badge(staffCount, 'text-gray-400') },
    { key: 'fleet', label: 'Fleet Overview', icon: <Server size={16} className="text-blue-400 shrink-0" />, onOpen: setShowFleetOverview!, locked: !unlockHasServer, lockHint: HINT_SERVER },
    { key: 'financials', label: 'Financials', icon: <BarChart2 size={16} className="text-emerald-400 shrink-0" />, onOpen: setShowFinancials, locked: !unlockHasServer, lockHint: HINT_SERVER },
    { key: 'upgrades', label: 'Upgrades', icon: <Settings size={16} className="text-gray-300 shrink-0" />, onOpen: setShowUpgrades, locked: !unlockHasServer, lockHint: HINT_SERVER },
    { key: 'goals', label: 'Goals', icon: <Activity size={16} className="text-green-400 shrink-0" />, onOpen: setShowGoals, locked: !unlockHasServer, lockHint: HINT_SERVER },
    { key: 'contracts', label: 'Contracts', icon: <FileText size={16} className="text-teal-400 shrink-0" />, onOpen: setShowContractsModal, locked: !unlockEarning, lockHint: HINT_EARNING, badge: badge(activeContractCount, 'text-teal-400') },
    { key: 'sitelayout', label: 'Site Layout', icon: <Building size={16} className="text-purple-400 shrink-0" />, onOpen: setShowSiteLayoutEditor, locked: !unlockEarning, lockHint: HINT_EARNING },
    { key: 'loans', label: 'Loans', icon: <CreditCard size={16} className="text-lime-400 shrink-0" />, onOpen: setShowLoansModal, locked: !unlockEarning, lockHint: HINT_EARNING, badge: badge(activeLoanCount, 'text-orange-400') },
  ].filter(t => t.key !== 'fleet' || !!setShowFleetOverview);

  const renderTool = (t: Tool) => (
    <button
      key={t.key}
      onClick={t.locked ? undefined : () => open(t.onOpen)}
      disabled={t.locked}
      title={t.locked ? t.lockHint : undefined}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-colors ${
        t.locked ? 'bg-gray-800/40 cursor-not-allowed' : 'bg-gray-800 hover:bg-gray-700'
      }`}
    >
      <span className={t.locked ? 'opacity-40' : ''}>{t.icon}</span>
      <span className="flex flex-col min-w-0">
        <span className={t.locked ? 'text-gray-500' : ''}>{t.label}</span>
        {t.locked && <span className="text-[10px] text-gray-600 leading-tight">{t.lockHint}</span>}
      </span>
      {t.locked ? <Lock size={13} className="ml-auto text-gray-600 shrink-0" /> : t.badge}
    </button>
  );

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-56 bg-gray-900 border-l border-gray-700 shadow-2xl z-50 flex flex-col transition-transform duration-200 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <span className="font-semibold text-white">Management</span>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          {/* Infrastructure */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">Infrastructure</p>
            <div className="space-y-1">{infrastructure.map(renderTool)}</div>
          </div>

          {/* Operations */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">Operations</p>
            <div className="space-y-1">{operations.map(renderTool)}</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManagementPanel;
